import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as debounce from 'lodash.debounce';
import QuickSearchResult from './QuickSearchResult';
import QuickSearchResultRecentItems from './QuickSearchResultRecentItems';
import { translate } from '../Services/translation';
import {
    query,
    setSearchQuery,
    toggleShowFullForm,
    handleKeyDown,
    handleClickSearch,
    hideSearchResults,
    getRecentItems,
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);

const QuickSearch = ({ isLoggedIn }) => {
    const { query: searchQuery, result, showResult, showFullForm, selectedItem, totalResults, recentItems } = useSelector(
        (state) => state.quickSearch
    );
    const [lastClickedNode, setLastClickedNode] = useState(null);
    const searchUrl = window.__litium.quickSearchUrl + (searchQuery.length > 0 ? `?q=${searchQuery}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const isSearchInputFocused = document.activeElement === searchInput.current;
    const dispatch = useDispatch();
    const previousSelectedItem = usePrevious(selectedItem);

    const clickHandler = useCallback((event) => {
        setLastClickedNode(event.target);
    }, []);
    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        // set query value if it is avaialble in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem !== previousSelectedItem) {
            const el = document.querySelector('.quick-search-result__item--selected');
            el &&
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
        }
    }, [selectedItem, previousSelectedItem]);

    useEffect(() => {
        if (!searchQuery && isLoggedIn && isSearchInputFocused) {
            getRecent();
        }
    }, [searchQuery]);

    const getRecent = () => {
        if (!searchQuery) {
            dispatch(getRecentItems('viewed', '/api/recentlyviewed'));
            dispatch(getRecentItems('searches', '/api/recentlysearched'));
        }
    };

    const showRecentItems =
        !showResult && isSearchInputFocused && (recentItems.searches.length > 0 || recentItems.viewed.length > 0) && !searchQuery;

    return (
        <div ref={searchContainer}>
            <div
                className="quick-search"
                role="search"
            >
                <a
                    className="quick-search__link--block"
                    rel="nofollow"
                    href="#"
                    title={translate('general.search')}
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(toggleShowFullForm());
                        setTimeout(() => {
                            searchInput.current && searchInput.current.focus();
                        }, 0);
                    }}
                >
                    <i className="quick-search__icon">
                        <SearchIcon />
                    </i>
                    <span className="quick-search__title">{translate('general.search')}</span>
                </a>
                <div
                    className={`quick-search__form ${showFullForm ? 'quick-search__form--force-show' : ''}`}
                    role="search"
                >
                    <button
                        onClick={() => {
                            showResult && dispatch(hideSearchResults());
                            dispatch(toggleShowFullForm());
                        }}
                    >
                        <ArrowBackIcon
                            className="quick-search__close-button"
                            sx={{ fontSize: 30 }}
                        />
                    </button>
                    <input
                        className="quick-search__input"
                        type="search"
                        placeholder={translate('general.search')}
                        autoComplete="off"
                        value={decodeURIComponent(searchQuery)}
                        onChange={(event) => {
                            const text = encodeURIComponent(event.target.value);
                            dispatch(setSearchQuery(text));
                            debouncedQuery(dispatch, text);
                        }}
                        onKeyDown={(event) => dispatch(handleKeyDown(event, { searchUrl }))}
                        ref={searchInput}
                        onBlur={() => {
                            if (searchContainer.current && !searchContainer.current.contains(lastClickedNode)) {
                                showResult && dispatch(hideSearchResults());
                                dispatch(toggleShowFullForm());
                            }
                        }}
                        onFocus={() => {
                            if (!searchQuery && isLoggedIn) {
                                getRecent();
                            }
                        }}
                    />
                    {searchQuery.length > 0 && (
                        <button
                            className="quick-search__clear-button"
                            type="button"
                            aria-label="Clear"
                            onClick={() => {
                                dispatch(setSearchQuery(''));
                                showResult && dispatch(hideSearchResults());
                                searchInput.current && searchInput.current.focus();
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </button>
                    )}
                    <button
                        className="quick-search__submit-button"
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            dispatch(handleClickSearch({ searchUrl }));
                        }}
                    >
                        <SearchIcon className="quick-search__submit-button--icon" />
                    </button>
                </div>
            </div>
            {showResult && (
                <QuickSearchResult
                    result={result}
                    selectedItem={selectedItem}
                    searchUrl={searchUrl}
                    totalResults={totalResults}
                />
            )}
            {showRecentItems && (
                <QuickSearchResultRecentItems
                    recentSearches={recentItems.searches}
                    recentlyViewed={recentItems.viewed}
                    searchUrl={searchUrl}
                />
            )}
        </div>
    );
};

export default QuickSearch;